import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, urgencyBadge } from "@/components/Common";
import { WarningIcon, CalendarCheckIcon, GasPumpIcon } from "@phosphor-icons/react";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/alerts");
        setAlerts(data);
      } catch (e) {
        console.error("Failed to load alerts:", e?.response?.data?.detail || e?.message || e);
      }
      setLoading(false);
    })();
  }, []);

  const overdue = alerts.filter((a) => a.urgency === "danger");
  const soon = alerts.filter((a) => a.urgency === "warning");
  const upcoming = alerts.filter((a) => a.urgency === "info");

  return (
    <div data-testid="alerts-page">
      <PageHeader eyebrow="Service queue" title="Alerts" subtitle="All overdue and upcoming services across your fleet." />

      <div className="p-8 space-y-6">
        {loading ? (
          <div className="font-mono text-sm text-slate-500">LOADING...</div>
        ) : alerts.length === 0 ? (
          <div className="bg-white border border-slate-300 rounded-sm p-12 text-center">
            <CalendarCheckIcon size={40} weight="thin" className="mx-auto text-green-600 mb-2" />
            <div className="font-display text-lg font-bold text-slate-900">All clear</div>
            <div className="text-sm text-slate-600 mt-1">No services are due or overdue.</div>
          </div>
        ) : (
          <>
            <AlertSection title="Overdue" tone="danger" alerts={overdue} testid="overdue-section" />
            <AlertSection title="Due soon (within 7 days)" tone="warning" alerts={soon} testid="due-soon-section" />
            <AlertSection title="Upcoming (within 30 days)" tone="info" alerts={upcoming} testid="upcoming-section" />
          </>
        )}
      </div>
    </div>
  );
}

function AlertSection({ title, tone, alerts, testid }) {
  if (alerts.length === 0) return null;
  return (
    <div className="bg-white border border-slate-300 rounded-sm" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <WarningIcon size={18} weight="fill" className={tone === "danger" ? "text-red-600" : tone === "warning" ? "text-amber-600" : "text-blue-600"} />
          <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
        </div>
        <span className="font-mono text-xs text-slate-500">{alerts.length} items</span>
      </div>
      <table className="fleet-table">
        <thead>
          <tr>
            <th>Urgency</th>
            <th>Truck</th>
            <th>Service</th>
            <th>Due date</th>
            <th>Days left</th>
            <th>Miles left</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a) => {
            const u = urgencyBadge(a.urgency);
            const isFuel = a.alert_kind === "fuel";
            return (
              <tr key={a.id} data-testid={`alert-${a.id}`}>
                <td><Badge tone={u.tone}>{u.label}</Badge></td>
                <td className="font-mono font-semibold">{a.truck_unit} · {a.truck_label}</td>
                <td className={isFuel ? "text-amber-800 font-semibold flex items-center gap-1" : "capitalize"}>
                  {isFuel && <GasPumpIcon size={14} weight="bold" />}
                  {isFuel ? `Low fuel — ${Number(a.fuel_percentage).toFixed(0)}%` : a.service_type.replace(/_/g, " ")}
                </td>
                <td className="font-mono">{isFuel ? (a.fuel_updated_at ? new Date(a.fuel_updated_at).toLocaleDateString() : "—") : (a.next_due_date?.slice(0, 10) || "—")}</td>
                <td className="font-mono">{isFuel ? "—" : (a.days_until !== null && a.days_until !== undefined ? (a.days_until < 0 ? `${Math.abs(a.days_until)} overdue` : `${a.days_until}`) : "—")}</td>
                <td className="font-mono">{isFuel ? "—" : (a.mileage_remaining !== null && a.mileage_remaining !== undefined ? Number(a.mileage_remaining).toLocaleString() : "—")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
